/**
 * WS-F — the one fetch primitive every R7 pane panel uses.
 *
 * ## ⛔ Why a PER-PANEL hook and not a per-screen loader
 *
 * WS-F §2.1 and §6 assertions 2–3: `cp:app:runtime:read` and `cp:app:adoption:read` are
 * DIFFERENT grants and a real principal can hold one without the other. A screen that loads
 * its panels through one combined promise turns a single 403 into a whole-page error — which
 * tells a viewer their app is broken when in truth one panel is simply not theirs to see.
 *
 * So each panel owns its own {@link AsyncState}, and {@link useApiResource} is deliberately
 * scoped to ONE request. The independence is structural rather than a rule someone has to
 * remember: there is no shared state for a failure to leak into.
 *
 * ## ⛔ The three absences are preserved as far as the renderer
 *
 * `empty` ≠ `errorstate` ≠ `notcollected` (WS-F §3 rule 1) starts here. `kind: 'error'`
 * carries the HTTP `status` alongside the message, because 403 renders differently from 500:
 * one is "not yours", the other is "we broke". Collapsing them to a boolean at this layer
 * makes the distinction unrecoverable downstream, and `notcollected` — which is a *producer*
 * statement, never a transport one — is decided by the panel from the payload, not here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../platform/http/client';
import { apiErrorMessage } from '../platform/errors';

/**
 * The lifecycle of one panel's read.
 *
 * ⛔ A discriminated union, NOT `{data, loading, error}`. The loose-bag shape admits
 * `loading: true` alongside a populated `data`, and every consumer then has to decide which
 * field wins — which is how a stale row renders under a spinner, or an error renders as an
 * empty table. Here the states are mutually exclusive by construction.
 */
export type AsyncState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: T }
  | {
      readonly kind: 'error';
      /**
       * HTTP status, or `null` when the failure never reached a response (a network fault,
       * or a non-JSON body). `null` is not 0 and not 500 — inventing a status would let a
       * caller branch on a code the server never sent.
       */
      readonly status: number | null;
      readonly message: string;
    };

/**
 * The count a tab shows: the row count once the read of `resource` is ready, else `null`, so the
 * tab shows no number while it loads or after a failure. Pure.
 */
export function readyCount(resource: ApiResource<readonly unknown[]>): number | null {
  return resource.state.kind === 'ready' ? resource.state.data.length : null;
}

/** `true` iff this failure is an authorization denial — the panel-degrades case (§6 #2/#3). */
export function isForbidden(state: AsyncState<unknown>): boolean {
  return state.kind === 'error' && state.status === 403;
}

/**
 * Read one endpoint, exposing {@link AsyncState} and an explicit `reload`.
 *
 * ## ⛔ `key` is the dependency, and it is a STRING on purpose
 *
 * `fetcher` is a closure, so it is a new function identity on every render; using it as the
 * effect dependency would re-fetch forever. `key` is the caller's declaration of *what*
 * is being fetched — normally the request URL — so the effect re-runs exactly when the
 * subject changes. WS-F §6 assertion 9 (Fleet must issue ONE request for N data planes) is
 * a direct consequence: one key ⇒ one fetch, regardless of how many rows come back.
 *
 * ## ⛔ There is no timer here, and there must not be
 *
 * WS-F §3.5 / §6 assertion 10: no poll, anywhere. Refresh is {@link ApiResource.reload},
 * driven by a control the operator clicks. A 5-second poll that looks like a tail but drops
 * the lines between requests is worse than an honest button, because the gap is invisible.
 */
export interface ApiResource<T> {
  readonly state: AsyncState<T>;
  /**
   * Re-issue the request without a change of `key`. By default the state returns to `loading`
   * first, so the UI cannot show stale-as-fresh. With {@link ApiResourceOptions.keepOnReload}, a
   * reload from `ready` keeps the value on screen and {@link refreshing} is `true` until the read
   * lands; a failed read then replaces the value with the `error` state.
   */
  readonly reload: () => void;
  /**
   * `true` while a {@link reload} runs and the last `ready` value stays on screen, else `false`.
   * Always `false` without {@link ApiResourceOptions.keepOnReload}. A pane that opts in keys its
   * busy line on this field.
   */
  readonly refreshing: boolean;
  /**
   * When the last `ready` answer of the CURRENT `key` landed, as an ISO string, or `null` while no
   * read of this key has resolved. This is what a "showing logs as of <time>" line renders (§3.5) —
   * the honest alternative to a follow mode: the reader is told the age of what they are looking at.
   * A reload or a failed read of the same key keeps it; a new key starts at `null`, so an answer of
   * another key never dates this key's state.
   */
  readonly loadedAt: string | null;
}

/** Options of {@link useApiResource}. */
export interface ApiResourceOptions {
  /**
   * Keep a `ready` value on screen through a {@link ApiResource.reload} of the same `key`, with
   * `refreshing` set until the read lands (#2662). Default `false`: a reload passes through
   * `loading`. Opt in only where no write control acts on the kept value during the re-read.
   * A key change, a first read and a retry from `error` pass through `loading` in both modes.
   */
  readonly keepOnReload?: boolean;
}

/** The state of one read, with the key it answers, so a render can tell an answer for another key. */
interface HeldState<T> {
  readonly key: string;
  readonly state: AsyncState<T>;
  readonly refreshing: boolean;
  /** The time of the last `ready` answer of `key`, or `null` when `key` has had none. */
  readonly loadedAt: string | null;
}

/**
 * Read one endpoint for `key`. The state it returns always belongs to the current `key`. The hook
 * holds the answer of a read with the key it answers. When a render passes a new key, the held
 * answer belongs to the old key, so the hook returns `loading` and `refreshing: false` for that render. The effect then issues the
 * read of the new key (2026-10-24-pool-machine-generations WS-2gb, review row WS-2c-P3-R19). Before
 * this rule, the first render after a data plane switch returned the old data plane's `ready` value,
 * and a panel keyed on the new id rendered the old rows under it for one commit.
 *
 * `loadedAt` belongs to the key too: it is the time of the last `ready` answer of the current key,
 * and `null` for a key with none (WS-2gb fix 2). Before this rule, a read of a new key that was
 * still running or had failed carried the time of the old key's answer.
 */
export function useApiResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: ApiResourceOptions = {},
): ApiResource<T> {
  const [held, setHeld] = useState<HeldState<T>>(() => ({ key, state: { kind: 'loading' }, refreshing: false, loadedAt: null }));
  /*
   * The `loading` state a render returns while the held answer belongs to another key: one object per
   * key, so the renders of one key before its effect runs compare equal, and a render of the next key
   * returns another object. Each read that starts in the effect sets a new `loading` object too. A
   * consumer that tells reads apart by state identity (`useHeld` in `alerts/shared.tsx`) so sees a new
   * state for each key. With one object for every key, a switch from B to C before B's read started
   * kept B's object, and `useHeld` read C's answer as the end of B's read (S2 push fix 1).
   */
  const loading = useMemo<AsyncState<T>>(() => {
    // The factory reads `key`, so the hook rule counts the dependency that gives each key its object.
    void key;
    return { kind: 'loading' };
  }, [key]);
  const current: HeldState<T> = held.key === key ? held : { key, state: loading, refreshing: false, loadedAt: null };
  const state = current.state;
  /*
   * `stateRef` holds the state of the latest render. `keyRef` holds the key of the last read, and the
   * effect sets it, so the effect can tell a reload from a key change.
   */
  const stateRef = useRef(state);
  stateRef.current = state;
  const keyRef = useRef(key);
  const keepOnReloadRef = useRef(options.keepOnReload === true);
  keepOnReloadRef.current = options.keepOnReload === true;
  /**
   * Bumped by `reload` to re-run the effect without changing `key`.
   *
   * ⚠️ The effect body — not `reload` itself — issues the request. A `reload` that fetched
   * directly would race the effect on mount and double-fire, which is precisely what
   * assertion 9 counts.
   */
  const [nonce, setNonce] = useState(0);

  /**
   * ⛔ The fetcher is held in a REF rather than named as an effect dependency.
   *
   * It is a closure, so it has a new identity every render; listing it would re-fetch on
   * every render forever. The alternative — listing it and silencing the lint — would be a
   * suppression comment naming an ESLint rule, and there is no ESLint in this repo (oxlint
   * only, per CLAUDE.md), so the comment would be inert decoration over a real hazard. A ref
   * removes the hazard instead of hiding it: the effect always calls the LATEST fetcher, and
   * `key` alone decides when it re-runs.
   */
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    /**
     * ⛔ Guards against the resolved-after-unmount write AND against out-of-order
     * responses when `key` changes mid-flight. Without it, switching data planes twice
     * quickly can settle the FIRST request last and render the wrong DP's pods under the
     * newly-selected DP's heading — a wrong answer that looks entirely plausible.
     */
    let live = true;
    // With keepOnReload, a reload from `ready` keeps the value on screen. Every other read shows `loading`.
    const keep = keepOnReloadRef.current && keyRef.current === key && stateRef.current.kind === 'ready';
    keyRef.current = key;
    // A reload of the same key keeps its `loadedAt`; a new key starts with none.
    setHeld((before) =>
      keep
        ? { ...before, refreshing: true }
        : { key, state: { kind: 'loading' }, refreshing: false, loadedAt: before.key === key ? before.loadedAt : null },
    );
    fetcherRef.current().then(
      (data) => {
        if (!live) return;
        setHeld({ key, state: { kind: 'ready', data }, refreshing: false, loadedAt: new Date().toISOString() });
      },
      (error: unknown) => {
        if (!live) return;
        // The start above set `key`, so `before` holds this key's `loadedAt`.
        setHeld((before) => ({
          key,
          state: {
            kind: 'error',
            status: error instanceof ApiError ? error.status : null,
            message: apiErrorMessage(error, 'The request failed.'),
          },
          refreshing: false,
          loadedAt: before.loadedAt,
        }));
        // ⛔ `loadedAt` is NOT stamped on failure. It describes the data on screen, and a
        // failed read put nothing there; advancing it would date a panel that has no
        // content, making a stale success look freshly confirmed.
      },
    );
    return () => {
      live = false;
    };
  }, [key, nonce]);

  return { state, reload, loadedAt: current.loadedAt, refreshing: current.refreshing };
}
